/**
 * ProgramWizard Component
 * Multi-step wizard for creating/editing programs
 */

import React, { useState, useEffect } from 'react';
import { FaTimes, FaChevronLeft, FaChevronRight, FaCheck } from 'react-icons/fa';
import { ClipLoader } from 'react-spinners';

import { useWizardState } from './hooks/useWizardState';
import BasicsStep from './steps/BasicsStep';
import ScheduleStep from './steps/ScheduleStep';
import PricingStep from './steps/PricingStep';
import DetailsStep from './steps/DetailsStep';
import { scorecardTemplateApi, programScorecardApi } from '../../api/scorecard';

import styles from './ProgramWizard.module.css';

const STEPS = [
  { id: 1, title: 'Basics', icon: '📋' },
  { id: 2, title: 'Schedule', icon: '📅' },
  { id: 3, title: 'Pricing', icon: '💰' },
  { id: 4, title: 'Details', icon: '📝' }
];

const ProgramWizard = ({ isOpen, onClose, onSubmit, clubId, initialData }) => {
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [gameFormats, setGameFormats] = useState({});
  
  const {
    currentStep,
    formData,
    errors,
    isSubmitting,
    setIsSubmitting,
    updateField,
    updateArrayItem,
    addArrayItem,
    removeArrayItem,
    nextStep,
    prevStep,
    goToStep,
    resetForm,
    getSubmitData
  } = useWizardState(initialData);

  // Fetch categories
  useEffect(() => {
    if (isOpen && clubId) {
      fetchCategories();
      fetchGameFormats();
    }
  }, [isOpen, clubId]);

  // Reset form when opening for new program
  useEffect(() => {
    if (isOpen && !initialData) {
      resetForm();
    }
  }, [isOpen, initialData]);

  const fetchCategories = async () => {
    try {
      setCategoriesLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/categories/club/${clubId}`
      );
      if (!response.ok) throw new Error('Failed to fetch categories');
      const data = await response.json();
      // Filter only enabled categories
      const enabledCategories = data.filter(cat => cat.isEnabled !== false);
      setCategories(enabledCategories);
      
      // Set default category if none selected
      if (!formData.category && enabledCategories.length > 0) {
        const defaultCategory = enabledCategories.find(cat => cat.name === 'Recreational') || enabledCategories[0];
        updateField('category', defaultCategory._id);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const fetchGameFormats = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/programs/game-formats`
      );
      if (!response.ok) throw new Error('Failed to fetch game formats');
      const data = await response.json();
      setGameFormats(data);
    } catch (err) {
      console.error('Error fetching game formats:', err);
      // Will fall back to local constants
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      const submitData = getSubmitData();
      submitData.clubId = clubId;

      const url = initialData
        ? `${process.env.REACT_APP_BACKEND_URL}/api/programs/${initialData._id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/programs`;

      const method = initialData ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save program');
      }

      const data = await response.json();
      
      // Auto-configure default scorecard for new programs
      if (!initialData && data._id) {
        try {
          const sport = submitData.sport || 'Soccer';
          // Get templates for this sport
          const sportTemplates = await scorecardTemplateApi.getTemplatesBySport(sport);
          let templateId = null;
          
          // Find a template with periods and timer enabled (best for live scoring)
          if (sportTemplates?.templates?.length > 0) {
            // Prefer a template that has periods enabled
            const templateWithPeriods = sportTemplates.templates.find(
              t => t.schema?.periods?.enabled && t.schema?.timer?.enabled
            );
            templateId = templateWithPeriods?._id || sportTemplates.templates[0]._id;
          }
          
          // If no sport-specific template, try generic "Other"
          if (!templateId) {
            const genericTemplates = await scorecardTemplateApi.getTemplatesBySport('Other');
            if (genericTemplates?.templates?.length > 0) {
              templateId = genericTemplates.templates[0]._id;
            }
          }
          
          // Configure the scorecard if we found a template
          if (templateId) {
            await programScorecardApi.configureScorecard(data._id, templateId);
            console.log('Auto-configured default scorecard for program:', data._id);
          }
        } catch (scorecardError) {
          // Don't fail program creation if scorecard config fails
          console.warn('Could not auto-configure scorecard:', scorecardError);
        }
      }
      
      onSubmit(data);
      onClose();
    } catch (error) {
      console.error('Error saving program:', error);
      alert(error.message || 'Failed to save program. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    if (currentStep === 4) {
      handleSubmit();
    } else {
      nextStep();
    }
  };

  const handleClose = () => {
    if (formData.name || formData.category !== categories[0]?._id) {
      if (window.confirm('Are you sure you want to close? Your changes will be lost.')) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  if (!isOpen) return null;

  const renderStep = () => {
    const stepProps = {
      formData,
      errors,
      updateField,
      updateArrayItem,
      addArrayItem,
      removeArrayItem,
      clubId
    };

    switch (currentStep) {
      case 1:
        return (
          <BasicsStep
            {...stepProps}
            categories={categories}
            categoriesLoading={categoriesLoading}
            gameFormats={gameFormats}
          />
        );
      case 2:
        return <ScheduleStep {...stepProps} />;
      case 3:
        return <PricingStep {...stepProps} />;
      case 4:
        return <DetailsStep {...stepProps} />;
      default:
        return null;
    }
  };

  const isLastStep = currentStep === 4;
  const canSkipToEnd = currentStep === 4;

  return (
    <div className={styles.overlay}>
      <div className={styles.wizard}>
        {/* Header */}
        <div className={styles.header}>
          <h2 className={styles.title}>
            {initialData ? 'Edit Program' : 'Create Program'}
          </h2>
          <button className={styles.closeBtn} onClick={handleClose}>
            <FaTimes />
          </button>
        </div>

        {/* Progress Steps */}
        <div className={styles.progress}>
          {STEPS.map((step, index) => (
            <React.Fragment key={step.id}>
              <button
                className={`${styles.step} ${currentStep === step.id ? styles.active : ''} ${currentStep > step.id ? styles.completed : ''}`}
                onClick={() => goToStep(step.id)}
                disabled={step.id > currentStep && currentStep < step.id}
              >
                <span className={styles.stepIcon}>
                  {currentStep > step.id ? <FaCheck /> : step.icon}
                </span>
                <span className={styles.stepTitle}>{step.title}</span>
              </button>
              {index < STEPS.length - 1 && (
                <div className={`${styles.connector} ${currentStep > step.id ? styles.completed : ''}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step Content */}
        <div className={styles.content}>
          {renderStep()}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <div className={styles.footerLeft}>
            {currentStep > 1 && (
              <button
                type="button"
                className={styles.backBtn}
                onClick={prevStep}
                disabled={isSubmitting}
              >
                <FaChevronLeft /> Back
              </button>
            )}
          </div>

          <div className={styles.footerRight}>
            {currentStep === 4 && !initialData && (
              <span className={styles.optionalHint}>
                All fields on this step are optional
              </span>
            )}
            
            <button
              type="button"
              className={styles.cancelBtn}
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>

            <button
              type="button"
              className={`${styles.nextBtn} ${isLastStep ? styles.submitBtn : ''}`}
              onClick={handleNext}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <ClipLoader color="#1a1a2e" size={16} />
              ) : isLastStep ? (
                <>
                  <FaCheck /> {initialData ? 'Update Program' : 'Create Program'}
                </>
              ) : (
                <>
                  Next <FaChevronRight />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgramWizard;









